import { httpClient } from '@/services/client/http-client'

import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { getValue } from '@/lib/utils'
import { ApiResponse } from '@/types/api'
import type { Product } from '@/types/product'
import {
  ExtraFeature,
  ProductPromotion,
  ProductSuggestionResponse,
} from '@/types/product-detail'
import {
  SizeCalculationMaleParams,
  SizeMaleSuggestion,
  SizeTableMale,
  TCalculateSizeParams,
} from '@/types/size'
import { VoucherResponse } from '@/types/voucher'
import { ProductFormatter } from '../formatter/__f.product'
import { VoucherFormatter } from '../formatter/__f.voucher'

export const __cProductService = {
  getProductsByIds: async (ids: string[], limit?: number) => {
    const response = await httpClient.V1.get<ApiResponse<Product[]>>(
      `/products`,
      {
        params: {
          ids: ids.join(','),
          limit,
          visibility: true,
        },
      }
    )
    return response.data
  },

  searchProducts: async (keyword: string) => {
    const response = await httpClient.V1.get<ApiResponse<Product[]>>(
      `/products/home-search`,
      {
        params: {
          home_search: 1,
          limit: 4,
          page: 1,
          sort: 'created_at',
          keyword,
          visibility: true,
        },
      }
    )
    return response.data
  },

  getProductWornTogether: async (productId: string) => {
    const { data } = await httpClient.V3.get<ApiResponse<Product[]>>(
      `/products/${productId}/worn-together`,
      {
        params: {
          visibility: true,
        },
      }
    )
    const formattedData = ProductFormatter.reFormatProductResponse(data || [])
    return formattedData
  },

  getTechnologyBasedProducts: async (productId: string) => {
    const { data } = await httpClient.V3.get<ApiResponse<Product[]>>(
      `/products/tech-related?product_id=${productId}`,
      {
        params: {
          visibility: true,
        },
      }
    )
    const formattedData = ProductFormatter.reFormatProductResponse(data || [])
    return formattedData
  },

  getProductSizeSuggestion: async (params: TCalculateSizeParams) => {
    const response = await httpClient.V3.get<
      ApiResponse<{
        size: string
        products: Product[]
      }>
    >('/calculator-women-size-guide', { params })
    return response
  },

  getSizeTableBySlug: async (slug: string) => {
    const { data } = await httpClient.V1.get<ApiResponse<SizeTableMale>>(
      `/sizecharts/key/${slug}`
    )

    return data
  },

  getSizeMaleSuggestion: async (sizeData: SizeCalculationMaleParams) => {
    const { data } = await httpClient.V4_NEW.get<
      ApiResponse<SizeMaleSuggestion>
    >('/tools/size-male-suggest', { params: sizeData })
    return data
  },

  getProductVouchers: async (productId: string) => {
    const { data } = await httpClient.V3.get<ApiResponse<VoucherResponse>>(
      `/get-discount-product?product_id=${productId}`
    )

    return VoucherFormatter.transformVouchersResponse(data)
  },

  getProductPromotions: async (productId: string) => {
    const { data } = await httpClient.V4_NEW.get<
      ApiResponse<ProductPromotion[]>
    >(`/products/get-prize-product/${productId}`, {
      params: {
        visibility: true,
      },
    })
    const res = getValue(data, 'product_giveaway', []) as ProductPromotion[]
    const formattedData = ProductFormatter.reFormatProductPromotionResponse(
      res || []
    )
    return formattedData
  },

  saveRecentProduct: async (productId: string) => {
    const response = await httpClient.V4_NEW.post('/products/viewed', {
      product_id: productId,
      device_token: DeviceTokenHelper.get(),
    })
    return response
  },

  getRecentProducts: async (limit?: number) => {
    const { data } = await httpClient.V4_NEW.get<
      ApiResponse<{ product_ids: string[] }>
    >('/products/viewed', {
      params: {
        device_token: DeviceTokenHelper.get(),
        visibility: true,
      },
    })

    if (!data?.product_ids || data?.product_ids.length === 0) {
      return []
    }

    const res = await __cProductService.getProductsByIds(
      data?.product_ids,
      limit
    )
    return ProductFormatter.reFormatProductResponse(res || [])
  },

  getProductPricingPolicy: async (productId: string) => {
    const { data } = await httpClient.V4_NEW.get<
      ApiResponse<{ description: string }>
    >(`/products/get-pricing-policy-description/${productId}`)
    return data?.description || ''
  },

  getProductSuggestion: async (productId: string) => {
    const { data } = await httpClient.V3.get<
      ApiResponse<ProductSuggestionResponse>
    >(`/get-suggested-product?id=${productId}`)
    return ProductFormatter.reFormatProductResponse(data?.products || [])
  },

  getProductExtraFeatures: async (productId: string) => {
    const { data } = await httpClient.V4_NEW.get<
      ApiResponse<{ features: ExtraFeature[] }>
    >(`/products/get-extra-features/${productId}`)
    return data?.features || []
  },
}
